import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import type { RefObject } from 'react';
import { Button } from '@/app/components/button';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { PaginationDash } from '@/app/components/navigation/pagination';
import { LineupGroupNav } from '@/app/cv/components/sections/home/lineup/lineup-group-nav';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
export interface GroupPagination {
  groupStart: number;
  groupSize: number;
  currentPageInGroup: number;
  groupLabel: string | null;
}
interface LineupCarouselOverlayProps {
  categoryRef: RefObject<HTMLDivElement | null>;
  cubeInview: boolean;
  groupPagination: GroupPagination | null;
  canScrollPrevGroup: boolean;
  canScrollNextGroup: boolean;
  selectedIndex: number;
  totalModels: number;
  emblaApi: EmblaCarouselType | undefined;
  onGroupPageChange: (page: number) => void;
  onScrollPrevGroup: () => void;
  onScrollNextGroup: () => void;
}
export function LineupCarouselOverlay(t0) {
  const $ = _c(50);
  const {
    categoryRef,
    cubeInview,
    groupPagination,
    canScrollPrevGroup,
    canScrollNextGroup,
    selectedIndex,
    totalModels,
    emblaApi,
    onGroupPageChange,
    onScrollPrevGroup,
    onScrollNextGroup
  } = t0;
  let t1;
  if ($[0] !== groupPagination || $[1] !== onGroupPageChange) {
    t1 = !!groupPagination && groupPagination.groupSize > 1 && <PaginationDash theme="dark" totalPages={groupPagination.groupSize} currentPage={groupPagination.currentPageInGroup} onPageChange={onGroupPageChange} wrapperClassName="py-6 justify-center absolute w-full top-[calc(100%-24px)] pointer-events-auto" />;
    $[0] = groupPagination;
    $[1] = onGroupPageChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !cubeInview;
  let t3;
  if ($[3] !== t2) {
    t3 = cn("pointer-events-auto absolute top-[calc(100%+28px)] left-0 w-full text-center", "transition-[opacity,translate] delay-800 duration-600 ease-[cubic-bezier(0.33,1,0.68,1)]", {
      "translate-y-5 opacity-0": t2
    });
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = groupPagination?.groupLabel ?? "";
  let t5;
  if ($[5] !== canScrollNextGroup || $[6] !== canScrollPrevGroup || $[7] !== onScrollNextGroup || $[8] !== onScrollPrevGroup || $[9] !== t4) {
    t5 = <LineupGroupNav groupLabel={t4} canScrollPrevGroup={canScrollPrevGroup} canScrollNextGroup={canScrollNextGroup} onScrollPrev={onScrollPrevGroup} onScrollNext={onScrollNextGroup} />;
    $[5] = canScrollNextGroup;
    $[6] = canScrollPrevGroup;
    $[7] = onScrollNextGroup;
    $[8] = onScrollPrevGroup;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== categoryRef || $[12] !== t3 || $[13] !== t5) {
    t6 = <div ref={categoryRef} className={t3}>{t5}</div>;
    $[11] = categoryRef;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t1 || $[16] !== t6) {
    t7 = <div className="pointer-events-none px-6 sm:px-12 lg:px-0"><div className="aspect-4-3 relative mx-auto w-full max-w-144">{t1}{t6}</div></div>;
    $[15] = t1;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = !cubeInview;
  let t9;
  if ($[18] !== t8) {
    t9 = cn("center-y pointer-events-auto absolute left-0", "transition-[opacity,translate]! delay-1200 duration-600 ease-[cubic-bezier(0.33,1,0.68,1)]", {
      "translate-x-5 opacity-0": t8
    });
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconArrowBack />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = selectedIndex === 0;
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = createDataAttributes({
      test: "button-prev"
    });
    t13 = createDataGA({
      click: true,
      eventName: "lineup-carousel-prev",
      group: "lineup-carousel-prev",
      url: "#",
      text: "\u0E22\u0E49\u0E2D\u0E19\u0E2B\u0E25\u0E31\u0E07"
    });
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  let t14;
  if ($[23] !== emblaApi) {
    t14 = () => emblaApi?.scrollPrev();
    $[23] = emblaApi;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t11 || $[26] !== t14) {
    t15 = <Button iconBefore={t10} color="white" size="lg" className="p-4! shadow-md" disabled={t11} {...t12} {...t13} onClick={t14} />;
    $[25] = t11;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t9) {
    t16 = <div className={t9}>{t15}</div>;
    $[28] = t15;
    $[29] = t9;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = !cubeInview;
  let t18;
  if ($[31] !== t17) {
    t18 = cn("center-y pointer-events-auto absolute right-0", "transition-[opacity,translate]! delay-1200 duration-600 ease-[cubic-bezier(0.33,1,0.68,1)]", {
      "-translate-x-5 opacity-0": t17
    });
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <IconArrowForward />;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = selectedIndex === totalModels - 1;
  let t21;
  let t22;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = createDataAttributes({
      test: "button-next"
    });
    t22 = createDataGA({
      click: true,
      eventName: "lineup-carousel-next",
      group: "lineup-carousel-next",
      url: "#",
      text: "\u0E16\u0E31\u0E14\u0E44\u0E1B"
    });
    $[34] = t21;
    $[35] = t22;
  } else {
    t21 = $[34];
    t22 = $[35];
  }
  let t23;
  if ($[36] !== emblaApi) {
    t23 = () => emblaApi?.scrollNext();
    $[36] = emblaApi;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t20 || $[39] !== t23) {
    t24 = <Button iconBefore={t19} color="white" size="lg" className="p-4! shadow-md" disabled={t20} {...t21} {...t22} onClick={t23} />;
    $[38] = t20;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t18 || $[42] !== t24) {
    t25 = <div className={t18}>{t24}</div>;
    $[41] = t18;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== t16 || $[45] !== t25) {
    t26 = <div className="pointer-events-none absolute inset-x-6 inset-y-0 hidden lg:block">{t16}{t25}</div>;
    $[44] = t16;
    $[45] = t25;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t26 || $[48] !== t7) {
    t27 = <div className="pointer-events-none absolute top-0 left-0 w-full">{t7}{t26}</div>;
    $[47] = t26;
    $[48] = t7;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  return t27;
}